import { useEffect, useState } from "react";

import {
  GoogleMap,
  Marker,
  Polyline,
  DirectionsRenderer,
  useLoadScript,
  OverlayView,
} from "@react-google-maps/api";

import TravelDetails from "./travel-details";
import { FaMapMarker } from "react-icons/fa";
import { FaMapLocationDot } from "react-icons/fa6";

const customMapsStyles = [
  {
    elementType: "geometry",
    stylers: [
      {
        color: "#f5f5f5",
      },
    ],
  },
  {
    elementType: "labels.icon",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#616161",
      },
    ],
  },
  {
    elementType: "labels.text.stroke",
    stylers: [
      {
        color: "#f5f5f5",
      },
    ],
  },
  {
    featureType: "administrative.land_parcel",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#bdbdbd",
      },
    ],
  },
  {
    featureType: "poi",
    elementType: "geometry",
    stylers: [
      {
        color: "#eeeeee",
      },
    ],
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#757575",
      },
    ],
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [
      {
        color: "#e5e5e5",
      },
    ],
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#9e9e9e",
      },
    ],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [
      {
        color: "#ffffff",
      },
    ],
  },
  {
    featureType: "road.arterial",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#757575",
      },
    ],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [
      {
        color: "#dadada",
      },
    ],
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#616161",
      },
    ],
  },
  {
    featureType: "road.local",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#9e9e9e",
      },
    ],
  },
  {
    featureType: "transit.line",
    elementType: "geometry",
    stylers: [
      {
        color: "#e5e5e5",
      },
    ],
  },
  {
    featureType: "transit.station",
    elementType: "geometry",
    stylers: [
      {
        color: "#eeeeee",
      },
    ],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [
      {
        color: "#c9c9c9",
      },
    ],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#9e9e9e",
      },
    ],
  },
];

const containerStyle = {
  width: "100%",
  height: "550px",
};

function formatTime(dateString) {
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

export default function TravelMap({ data }) {
  const [stops, setStops] = useState([]);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [directions, setDirections] = useState(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyD-4Q_BPIHXnGv1dqlaLOBKMM3mZRrPy4E",
    libraries: ["maps", "marker", "geometry"],
  });

  // const heading = isLoaded
  //   ? new window.google.maps.geometry.spherical.computeHeading(
  //       new window.google.maps.LatLng(currentPosition.lat, currentPosition.lng),
  //       new window.google.maps.LatLng(stops[3].lat, stops[3].lng)
  //     )
  //   : 0;

  useEffect(() => {
    if (data.Lat && data.Lng) {
      setCurrentPosition({
        lat: parseFloat(data.Lat),
        lng: parseFloat(data.Lng),
      });
    }
  }, [data]);

  useEffect(() => {
    if (data.visits.length > 0) {
      const arr = data.visits
        .sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate))
        .map((o) => ({
          name: o.destination,
          status: o.status,
          time: formatTime(o.scheduledDate),
          coords: {
            lat: parseFloat(o.destinationLat),
            lng: parseFloat(o.destinationLng),
          },
          phoneNumber: o.phoneNumber,
        }));
      setStops(arr);
    }
  }, [data]);

  useEffect(() => {
    if (isLoaded && stops.length > 1) {
      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route(
        {
          origin: stops[0].coords,
          destination: stops[stops.length - 1].coords,
          waypoints: stops
            .slice(1, -1)
            .map((s) => ({ location: s.coords, stopover: true })),
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === "OK" && result) {
            setDirections(result);
          }
        }
      );
    }
  }, [isLoaded, stops]);

  return (
    <div className="relative">
      {isLoaded ? (
        <GoogleMap
          center={currentPosition}
          mapContainerStyle={containerStyle}
          zoom={11}
          options={{
            styles: customMapsStyles,
            streetViewControl: false,
            disableDefaultUI: true,
          }}
        >
          {/* Travel Details Card */}
          <div className="absolute left-5 top-5 z-10 bg-white rounded-xl shadow-lg w-96 p-5 space-y-3 hidden lg:block">
            <TravelDetails stops={stops} vehicalId={data.VehicleNumber} />
          </div>
          {stops.length > 0 && (
            <>
              {/* Driving Route */}
              {directions && (
                <>
                  <DirectionsRenderer
                    directions={directions}
                    options={{
                      suppressPolylines: true, // hide default polyline
                      suppressMarkers: true,
                    }}
                  />

                  <Polyline
                    path={directions.routes[0].overview_path}
                    options={{
                      strokeColor: "#3B778929", // semi-transparent greenish
                      strokeWeight: 8, // thicker
                      strokeOpacity: 0.8,
                    }}
                  />

                  {/* Top thin line (foreground) */}
                  <Polyline
                    path={directions.routes[0].overview_path}
                    options={{
                      strokeColor: "#000000", // black
                      strokeWeight: 2, // thinner
                      strokeOpacity: 1,
                    }}
                  />
                </>
              )}

              {/* Custom Markers */}
              {stops[0].coords && (
                <Marker
                  position={stops[0].coords}
                  icon={<FaMapMarker size={20} />}
                  zIndex={1}
                />
              )}

              {/* Custom Markers */}
              {stops.map((stop, idx) => (
                <OverlayView
                  key={idx}
                  position={stop.coords}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  getPixelPositionOffset={(width, height) => {
                    // if (idx === 0) {
                    //   return { x: -(width + 70), y: height + 20 };
                    // } else if (idx === stops.length - 1) {
                    //   return { x: -(width + 50), y: -(height + 90) };
                    // }
                    return {
                      x: -(width / 2), // center horizontally
                      y: -height, // place above the point
                    };
                  }}
                >
                  <div className="bg-white shadow-lg border rounded-lg pl-3 pr-4 py-2 w-72 text-xs text-gray-800 absolute z-50">
                    <div className="flex items-center gap-2">
                      <img
                        src={
                          idx === stops.length - 1
                            ? <FaMapLocationDot />
                            : <FaMapMarker size={20} />
                        }
                        alt="location-marker"
                        className="size-8"
                      />
                      <div>
                        <div className="font-semibold">{stop.name}</div>
                        <div className="text-gray-500">{stop.time}</div>
                      </div>
                    </div>
                  </div>
                </OverlayView>
              ))}

              {stops[stops.length - 1].coords && (
                <Marker
                  position={stops[stops.length - 1].coords}
                  icon={{
                    url: <FaMapLocationDot />,
                  }}
                  zIndex={1}
                />
              )}
              {/* <Marker
            position={currentPosition}
            icon={{
              url: "/car.svg",
              scaledSize: new window.google.maps.Size(40, 40),
              anchor: new window.google.maps.Point(20, 20),
              rotation: 180,
            }}
          /> */}
            </>
          )}
          {currentPosition && (
            <OverlayView
              position={currentPosition}
              mapPaneName={OverlayView.OVERLAY_LAYER}
              getPixelPositionOffset={(w, h) => ({ x: -w / 2, y: -h - 31 })}
            >
              <div>
                <img
                  src="/car.svg" // put your custom car svg/png in /public
                  alt="car"
                  className="bg-white size-8 rounded-full"
                  style={{
                    // transform: `rotate(${heading}deg)`,
                    transformOrigin: "center center",
                    transition: "transform 0.3s linear",
                  }}
                />
              </div>
            </OverlayView>
          )}
        </GoogleMap>
      ) : (
        <div>...Loading</div>
      )}
    </div>
  );
}
